import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BrainCircuit, LayoutGrid, Rocket, Sparkles } from "lucide-react";
import { useEffect } from "react";
import { Orb } from "@/components/Orb";
import { WTCLogo } from "@/components/WTCLogo";

export default function Landing() {
  const [params] = useSearchParams();
  // ?go=build|reimagine auto-redirects participants to the right experience
  const go = params.get("go");

  useEffect(() => {
    if (go === "build" || go === "reimagine") {
      const t = setTimeout(() => {
        window.location.href = `/${go}`;
      }, 1600);
      return () => clearTimeout(t);
    }
  }, [go]);

  if (go === "build" || go === "reimagine") {
    return <ParticipantHandoff target={go} />;
  }
  return <PresenterHub />;
}

function ParticipantHandoff({ target }: { target: "build" | "reimagine" }) {
  const copy =
    target === "build"
      ? {
          tag: "Session 01 · AI Teammates",
          title: "Build your",
          accent: "AI teammate",
          sub: "In 3 minutes, design an AI that takes the first 80% off one repetitive task in your job.",
          cta: "Start building",
        }
      : {
          tag: "Session 02 · Reimagine the business",
          title: "Reimagine your",
          accent: "business",
          sub: "Three sentences in. Three bold AI moves out. One you'll act on this month.",
          cta: "Begin",
        };

  return (
    <div className="relative min-h-dvh flex flex-col items-center justify-center px-6 py-10 text-center overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <Orb size={200} intensity="thinking" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="mt-10 max-w-md"
      >
        <div className="inline-flex items-center gap-1.5 border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/60">
          <Sparkles className="h-3 w-3" /> {copy.tag}
        </div>
        <h1 className="mt-4 text-4xl font-bold leading-[1.05] text-white sm:text-5xl">
          {copy.title}{" "}
          <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
            {copy.accent}
          </span>
        </h1>
        <p className="mt-4 text-base leading-relaxed text-white/70">{copy.sub}</p>
        <Link to={`/${target}`} className="btn-primary mt-8">
          {copy.cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </div>
  );
}

function PresenterHub() {
  return (
    <div className="relative min-h-dvh px-6 py-10 overflow-hidden">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <WTCLogo />
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/40">Presenter hub</div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 text-center"
        >
          <h1 className="text-5xl font-bold leading-[1.05] text-white sm:text-6xl">
            You can build the
            <br />
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
              future today.
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60">
            Live demo for the WTC Lisboa keynote. Pick an experience to open its QR on the big screen.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ExperienceCard
            tag="Session 01"
            title="Build your AI teammate"
            desc="Each attendee designs a personal AI teammate for one task — name, prompt, plan."
            href="/stage?exp=build&tab=qr"
            Icon={BrainCircuit}
            delay={0.15}
          />
          <ExperienceCard
            tag="Session 02"
            title="Reimagine your business"
            desc="Three sentences in. Three bold AI moves out. One they'll act on this month."
            href="/stage?exp=reimagine&tab=qr"
            Icon={Rocket}
            delay={0.23}
          />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <Link to="/stage?exp=build&tab=marketplace" className="btn-ghost justify-start">
            <LayoutGrid className="h-4 w-4" />
            AI teammate marketplace
          </Link>
          <Link to="/stage?exp=reimagine&tab=marketplace" className="btn-ghost justify-start">
            <Sparkles className="h-4 w-4" />
            Bold-moves marketplace
          </Link>
        </motion.div>

        <div className="mt-16 border-t border-white/5 pt-6 text-center text-[11px] uppercase tracking-widest text-white/30">
          Live demo for WTC Lisboa · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

function ExperienceCard({
  tag,
  title,
  desc,
  href,
  Icon,
  delay,
}: {
  tag: string;
  title: string;
  desc: string;
  href: string;
  Icon: React.ComponentType<{ className?: string }>;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
    >
      <Link
        to={href}
        className="glass glow-border group block rounded-xl p-7 transition hover:bg-white/[0.06]"
      >
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-white/50">{tag}</div>
          <Icon className="h-5 w-5 text-white/40 transition group-hover:text-cyan-glow" />
        </div>
        <div className="mt-4 text-2xl font-semibold leading-tight text-white">{title}</div>
        <p className="mt-2 text-sm text-white/60">{desc}</p>
        <div className="mt-5 flex items-center gap-1.5 text-sm text-cyan-glow">
          Open QR screen
          <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </div>
      </Link>
    </motion.div>
  );
}
